import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class BillingPopupExpiredTrialComponent extends PopupInnerModalComponent<PopupData.None> {
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;

	get currentPlanName(): string | undefined {
		return this.billingService.activePlan?.name;
	}

	get title(): string {
		const currentUserFirstName = this.currentUserService.model?.firstName ?? '';
		return `${currentUserFirstName}, your free trial ${this.currentPlanName ? `of the <strong>${this.currentPlanName} plan</strong> ` : ' '}has ended`;
	}

	get isAdmin(): boolean {
		return this.currentUserService.isAdmin;
	}

	get isAgencyAdminInClientSpace(): boolean {
		return (
			this.accountService.isClient &&
			this.mmUsersService.agencyAdminRecordForClientSpace.id === this.currentUserService.id
		);
	}

	@action
	bookACall(): void {
		this.analyticsService.event(TrackingEvent.CALL_INITIATED, { origin: 'trial_expired' });
		window.open(this.accountService.bookACallLink, '_blank');
	}

	@action
	selectPlan(): void {
		this.navigationService.transitionToRoute('settings.billing.plans');
	}

	@action
	redirectToAgencySpace(): void {
		this.mmSpacesService.redirectToAgencySpace();
	}

	<template>
		<div class="content">
			<CommonUiIconComponent @name="Clock" @size="2xl" />
			<h3 class="content__title">{{{this.title}}}</h3>
			<p>
				To keep using Databox, select one of our plans and choose the right subscription for
				you. Data is no longer being updated from your connected data sources.
			</p>
			<div class="controls2">
				<div class="controls2__right">
					{{#if this.isAgencyAdminInClientSpace}}
						<SubmitButtonComponent
							@text="Switch to organization"
							@icon="ClientSwitch"
							@onTrigger={{this.redirectToAgencySpace}}
						/>
					{{else if this.isAdmin}}
						<SubmitButtonComponent
							@text="Select a plan"
							@icon="Growth"
							@onTrigger={{this.selectPlan}}
						/>
					{{/if}}
					<SubmitButtonComponent
						@text="Book a call"
						@theme="blue-outlined"
						@icon="Help"
						@onTrigger={{this.bookACall}}
					/>
				</div>
			</div>
		</div>
	</template>
}
